// Movorra ECAP client — talks to the ECAP flow-automation service (movorra-ecap Spring Boot).
// Studio (authoring) endpoints live under /studio/api and are tenant-scoped: in the ECAP `secure`
// profile the tenant is derived from the Cognito JWT (Authorization: Bearer), and in the dev profile
// from an X-Tenant-Id header — so we send BOTH (each profile ignores the other). The public chat
// runtime (/runtime) is gated by a per-flow endpoint key, not auth.
//
// Base URL from REACT_APP_ECAP_API_URL (default http://localhost:8080); dev tenant from
// REACT_APP_ECAP_TENANT (default 'acme'). See src/services/README.md.
// ECAP talks to its OWN backend. It uses the Studio-pool session (custom:tenantId / movorra_<tenant> groups), except
// for a Movorra super-admin on the single ride-share login (see setStudioAuthSource below).
import { getCurrentEcapSession, getCurrentSession } from './authService';

// PLATFORM-SSO-1: which login Studio calls carry. 'studio' (default) = the Studio pool session. 'rideshare' = a
// Movorra super-admin's single ride-share login, which movorra-flows accepts only for web-app id tokens carrying
// movorra-admin. Set by App.js before StudioApp mounts; a fresh token is read from that pool on every call.
let studioAuthSource = 'studio';
export function setStudioAuthSource(source) {
  studioAuthSource = source === 'rideshare' ? 'rideshare' : 'studio';
}
export const getStudioAuthSource = () => studioAuthSource;

export const ECAP_BASE = process.env.REACT_APP_ECAP_API_URL || 'http://localhost:8080';

// B5: the hosted, linkable customer chat page for a flow key (ECAP serves it at /embed/chat-page.html).
// Link it from "Open chat", email/SMS, a QR code on a flyer — no app deploy needed.
export const chatPageUrl = (publicKey, title) => {
  const q = new URLSearchParams({ key: publicKey });
  if (title) q.set('title', title);
  return `${ECAP_BASE}/embed/chat-page.html?${q.toString()}`;
};

// B5: the copy-paste website embed for a flow key (floating chat bubble via /embed/widget.js).
export const embedSnippet = (publicKey) =>
  `<script src="${ECAP_BASE}/embed/widget.js" data-key="${publicKey}"></script>`;
const DEV_TENANT = process.env.REACT_APP_ECAP_TENANT || 'acme';
const TENANT_STORE = 'ecap.activeTenant';

/** The tenant the dashboard is currently acting as (a super-admin can switch; persisted locally). */
export function activeTenant() {
  try { return localStorage.getItem(TENANT_STORE) || DEV_TENANT; } catch (e) { return DEV_TENANT; }
}
export function setActiveTenant(tenantId) {
  try { localStorage.setItem(TENANT_STORE, tenantId || DEV_TENANT); } catch (e) { /* ignore */ }
}

async function authHeaders(authSession) {
  let token = authSession?.idToken;
  const current = studioAuthSource === 'rideshare' ? getCurrentSession : getCurrentEcapSession;
  try { const s = await current(); token = s.idToken; } catch (e) { /* dev / mock session */ }
  const tenant = activeTenant();
  // Dev profile reads X-Tenant-Id; a secure-profile super-admin acts-as via X-Act-As-Tenant. Send both —
  // each profile honours the one that applies and ignores the other.
  const headers = { 'Content-Type': 'application/json', 'X-Tenant-Id': tenant, 'X-Act-As-Tenant': tenant };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

async function doFetch(base, path, headers, opts) {
  const res = await fetch(`${base}${path}`, { headers, ...opts });
  if (!res.ok && res.status !== 204) {
    let detail = `ECAP request failed (${res.status})`;
    try { const b = await res.json(); detail = b.error || detail; } catch (e) { /* non-JSON */ }
    // Attach the numeric HTTP status so callers can branch on it reliably (e.g. 409 in-use / import
    // collision, 400 validation). Once the backend sends a `{error}` JSON body, `detail` is the
    // human-readable message and NO LONGER contains the status code — substring-matching "409" on the
    // message silently stops working, so callers must read err.status instead. Backward-compatible:
    // existing `e.message` reads are unchanged.
    const err = new Error(detail);
    err.status = res.status;
    throw err;
  }
  if (res.status === 204) return null;
  return res.json().catch(() => null);
}

// Control-plane routing: which API base serves the active company. SHARED tenants (all of them, today)
// resolve to the shared base ECAP_BASE; a DEDICATED tenant resolves to its own stack's URL. The lookup
// itself always hits ECAP_BASE — the tenant registry lives on the shared control plane. Cached per tenant.
const ENDPOINT_CACHE = {};
export function clearEndpointCache(tenant) {
  if (tenant) delete ENDPOINT_CACHE[tenant];
  else Object.keys(ENDPOINT_CACHE).forEach((k) => delete ENDPOINT_CACHE[k]);
}
async function resolveBase(tenant, authSession) {
  if (tenant in ENDPOINT_CACHE) return ENDPOINT_CACHE[tenant] || ECAP_BASE;
  try {
    const headers = await authHeaders(authSession);
    const res = await fetch(`${ECAP_BASE}/studio/api/tenant-endpoint`, { headers });
    if (res.ok) {
      const b = await res.json();
      const base = (b && b.effectiveBaseUrl) || '';
      // ECAPAPI-2: cache ONLY a successful resolve. A SHARED tenant legitimately returns '' (→ ECAP_BASE),
      // which is safe to memoize. A transient non-2xx / network failure must NOT be cached as '' — otherwise
      // a DEDICATED tenant gets pinned to the shared stack for the whole session with no retry (wrong backend
      // for every subsequent flow read/write). On failure we fall through and re-attempt on the next call.
      ENDPOINT_CACHE[tenant] = base;
      return base || ECAP_BASE;
    }
  } catch (e) { /* transient — do not cache; retry on the next studio() call */ }
  return ECAP_BASE;
}

// Tenant-scoped data calls route to the active company's own stack (the shared cluster for SHARED tenants).
async function studio(path, opts, authSession) {
  const headers = await authHeaders(authSession);
  const base = await resolveBase(activeTenant(), authSession);
  return doFetch(base, path, headers, opts);
}

// Control-plane / fleet calls (tenant registry, endpoints, migration) ALWAYS hit the shared base — they
// manage the whole fleet and must never be routed to a single dedicated tenant's stack.
async function control(path, opts, authSession) {
  const headers = await authHeaders(authSession);
  return doFetch(ECAP_BASE, path, headers, opts);
}

// ---- Studio (authoring) — authenticated / tenant-scoped ----
export const listFlows = (authSession) => studio('/studio/api/flows', {}, authSession);
export const getFlow = (id, authSession) => studio(`/studio/api/flows/${encodeURIComponent(id)}`, {}, authSession);
// Upsert a flow (create or replace). The path id is authoritative.
export const saveFlow = (flow, authSession) =>
  studio(`/studio/api/flows/${encodeURIComponent(flow.id)}`, { method: 'PUT', body: JSON.stringify(flow) }, authSession);
// DELETE-LIVE-1: the backend refuses (409) to delete a flow that is currently PUBLISHED/live unless
// ?force=true — pass force only after the author has explicitly confirmed a live delete.
export const deleteFlow = (id, authSession, force = false) =>
  studio(`/studio/api/flows/${encodeURIComponent(id)}${force ? '?force=true' : ''}`, { method: 'DELETE' }, authSession);

// Governance: per-flow access level (EDITABLE / READ_ONLY / API_ONLY) — super-admin sets it.
export const getFlowAccessLevels = (a) => studio('/studio/api/flows/access', {}, a);
export const setFlowAccessLevel = (id, level, a) =>
  studio(`/studio/api/flows/${encodeURIComponent(id)}/access`, { method: 'PUT', body: JSON.stringify({ level }) }, a);

// Per-flow LLM model override (blank → platform default). Editor/admin only.
export const setFlowModel = (id, model, a) =>
  studio(`/studio/api/flows/${encodeURIComponent(id)}/model`, { method: 'PUT', body: JSON.stringify({ model }) }, a);

// AI Flow Tester — the LLM role-plays a persona through the flow (dry-run, no real actions) so you can
// see before publishing whether it completes or gets stuck.
export const simulateFlow = (id, persona, maxTurns, a) =>
  studio(`/studio/api/flows/${encodeURIComponent(id)}/simulate`, { method: 'POST', body: JSON.stringify({ persona, maxTurns }) }, a);

// Publish / version / rollback
export const getVersions = (id, a) => studio(`/studio/api/flows/${encodeURIComponent(id)}/versions`, {}, a);
export const rollbackFlow = (id, version, a) =>
  studio(`/studio/api/flows/${encodeURIComponent(id)}/rollback/${version}`, { method: 'POST' }, a);
export const compileFlow = (id, a) => studio(`/studio/api/flows/${encodeURIComponent(id)}/compile`, { method: 'POST' }, a);

// Export / import (portable JSON)
export const exportFlow = (id, a) => studio(`/studio/api/flows/${encodeURIComponent(id)}/export`, {}, a);
export const importFlow = (id, doc, a) =>
  studio(`/studio/api/flows/import${id ? `?id=${encodeURIComponent(id)}` : ''}`, { method: 'POST', body: JSON.stringify(doc) }, a);

// ---- Reusable library (define-once / reference-everywhere) — same CRUD + where-used shape ----
function crud(coll) {
  const base = `/studio/api/${coll}`;
  return {
    list: (a) => studio(base, {}, a),
    get: (id, a) => studio(`${base}/${encodeURIComponent(id)}`, {}, a),
    save: (id, body, a) => studio(`${base}/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(body) }, a),
    remove: (id, a) => studio(`${base}/${encodeURIComponent(id)}`, { method: 'DELETE' }, a),
    usage: (id, a) => studio(`${base}/${encodeURIComponent(id)}/usage`, {}, a),
    // Probe a connection (mints an OAUTH2 token or pings the base URL) / run a validation tool.
    test: (id, a) => studio(`${base}/${encodeURIComponent(id)}/test`, { method: 'POST' }, a),
    testValue: (id, value, a) =>
      studio(`${base}/${encodeURIComponent(id)}/test?value=${encodeURIComponent(value)}`, { method: 'POST' }, a),
  };
}
export const connections = crud('connections');
export const capabilities = crud('capabilities');
export const policies = crud('policies');
export const aiProfiles = crud('ai-profiles');
export const validationTools = crud('validation-tools');
export const domainTables = crud('domain-tables');

// Code capabilities — admin-authored sandboxed functions exposed to flows. list/get/save + create,
// publish/version, test-run a draft, and invoke the published version (executes on the Lambda executor).
const CC = '/studio/api/code-capabilities';
export const listCodeCapabilities = (a) => studio(CC, {}, a);
export const getCodeCapability = (id, a) => studio(`${CC}/${encodeURIComponent(id)}`, {}, a);
export const createCodeCapability = (body, a) => studio(CC, { method: 'POST', body: JSON.stringify(body) }, a);
export const saveCodeCapability = (id, body, a) => studio(`${CC}/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(body) }, a);
export const publishCodeCapability = (id, a) => studio(`${CC}/${encodeURIComponent(id)}/publish`, { method: 'POST' }, a);
export const testCodeCapability = (id, input, a) => studio(`${CC}/${encodeURIComponent(id)}/test`, { method: 'POST', body: JSON.stringify(input || {}) }, a);
export const invokeCodeCapability = (id, input, a) => studio(`${CC}/${encodeURIComponent(id)}/invoke`, { method: 'POST', body: JSON.stringify(input || {}) }, a);
export const assistCodeCapability = (prompt, a) => studio(`${CC}/assist`, { method: 'POST', body: JSON.stringify({ prompt }) }, a);
export const deleteCodeCapability = (id, a) => studio(`${CC}/${encodeURIComponent(id)}`, { method: 'DELETE' }, a);

// Flow eval suite — saved test cases + a runner (drives each scenario through the chat with actions firing).
export const getEvalCases = (flowId, a) => studio(`/studio/api/flows/${encodeURIComponent(flowId)}/eval-cases`, {}, a);
export const setEvalCases = (flowId, cases, a) => studio(`/studio/api/flows/${encodeURIComponent(flowId)}/eval-cases`, { method: 'PUT', body: JSON.stringify(cases || []) }, a);
export const runEval = (flowId, cases, a) => studio(`/studio/api/flows/${encodeURIComponent(flowId)}/eval`, { method: 'POST', body: JSON.stringify(cases || []) }, a);

// Human-in-the-loop approvals inbox — flows blocked on a human://approve hook. List / open / resolve.
export const listApprovals = (status, a) => studio(`/studio/api/approvals${status ? `?status=${encodeURIComponent(status)}` : ''}`, {}, a);
export const getApproval = (id, a) => studio(`/studio/api/approvals/${encodeURIComponent(id)}`, {}, a);
export const resolveApproval = (id, decision, note, a) => studio(`/studio/api/approvals/${encodeURIComponent(id)}/resolve`, { method: 'POST', body: JSON.stringify({ decision, note }) }, a);

// ---- Movorra Live (real-time human handoff) ----
export const liveQueue = (a) => studio('/studio/api/live/queue', {}, a);
export const getLive = (id, a) => studio(`/studio/api/live/${encodeURIComponent(id)}`, {}, a);
export const claimLive = (id, a) => studio(`/studio/api/live/${encodeURIComponent(id)}/claim`, { method: 'POST' }, a);
export const sendLive = (id, text, a) => studio(`/studio/api/live/${encodeURIComponent(id)}/messages`, { method: 'POST', body: JSON.stringify({ text }) }, a);
export const handbackLive = (id, message, a) => studio(`/studio/api/live/${encodeURIComponent(id)}/handback`, { method: 'POST', body: JSON.stringify({ message: message || '' }) }, a);
// Long-poll for live events — near-instant through API Gateway (holds ~25s, returns on the first event).
export const liveLongPoll = (a) => studio('/studio/api/live/poll', {}, a);
// AI-assist: draft a suggested operator reply from the live transcript.
export const suggestLive = (id, a) => studio(`/studio/api/live/${encodeURIComponent(id)}/suggest`, { method: 'POST' }, a);

// Operator shares a file with the customer in a live chat (multipart; Bearer-authed studio endpoint).
export async function sendLiveFile(id, file, authSession) {
  const headers = await authHeaders(authSession);
  delete headers['Content-Type']; // let the browser set the multipart boundary
  const fd = new FormData(); fd.append('file', file);
  const res = await fetch(`${ECAP_BASE}/studio/api/live/${encodeURIComponent(id)}/attachments`, {
    method: 'POST', headers, body: fd,
  });
  if (!res.ok) {
    // opslive-sendfile-opaque-status: attach the status (and the backend's {error} message when present) so
    // the caller can give actionable guidance — e.g. a 413 "file too large" instead of an opaque failure.
    let detail = `Upload failed (${res.status})`;
    try { const b = await res.json(); detail = b.error || detail; } catch (e) { /* non-JSON */ }
    const err = new Error(detail);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

// Files a customer shared in a session (public runtime endpoint — keyed by the unguessable session id).
// ECAPAPI-3 / OPSLIVE-FILESTRIP: THROW on a non-2xx / network failure instead of coercing it to the SAME empty
// array as a session that genuinely has no files — otherwise a listing failure reads as "no files shared" and
// hides real customer uploads from the operator. Both callers (OpsLive) now distinguish the two.
export const sessionAttachments = (sessionId) =>
  fetch(`${ECAP_BASE}/runtime/sessions/${encodeURIComponent(sessionId)}/attachments`)
    .then((r) => { if (!r.ok) throw new Error(`attachments failed (${r.status})`); return r.json(); })
    .then((d) => d.attachments || []);
export const attachmentUrl = (sessionId, attId) =>
  `${ECAP_BASE}/runtime/sessions/${encodeURIComponent(sessionId)}/attachments/${encodeURIComponent(attId)}`;

// SSE over fetch (EventSource can't send the Bearer header) — a BEST-EFFORT enhancement for instant events.
// Calls onEvent(type, data) per frame; returns abort(). If streaming isn't available end-to-end (e.g. the
// edge returns 503 for long-lived /studio responses), it gives up quietly after a few tries — callers poll
// as the reliable baseline, so the console still works. onFail (optional) is called when it gives up.
export async function openLiveStream(path, onEvent, authSession, onFail) {
  const ctrl = new AbortController();
  let stopped = false;
  let fails = 0;
  const run = async () => {
    while (!stopped && fails < 3) {
      try {
        const headers = await authHeaders(authSession);
        const base = await resolveBase(activeTenant(), authSession);
        const res = await fetch(`${base}${path}`, { headers, signal: ctrl.signal });
        if (!res.ok || !res.body) throw new Error(`stream ${res.status}`);
        fails = 0; // connected
        const reader = res.body.getReader();
        const dec = new TextDecoder();
        let buf = '';
        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          buf += dec.decode(value, { stream: true });
          let i;
          while ((i = buf.indexOf('\n\n')) >= 0) {
            const frame = buf.slice(0, i); buf = buf.slice(i + 2);
            let ev = 'message'; let data = '';
            frame.split('\n').forEach((l) => {
              if (l.startsWith('event:')) ev = l.slice(6).trim();
              else if (l.startsWith('data:')) data += l.slice(5).trim();
            });
            if (data) { try { onEvent(ev, JSON.parse(data)); } catch (e) { onEvent(ev, data); } }
          }
        }
      } catch (e) { if (stopped || ctrl.signal.aborted) return; fails += 1; }
      if (!stopped && fails < 3) await new Promise((r) => setTimeout(r, 3000));
    }
    if (!stopped && typeof onFail === 'function') onFail(); // streaming unavailable — caller relies on polling
  };
  run();
  return () => { stopped = true; ctrl.abort(); };
}

// Domain-table CSV bulk import (raw text body).
export async function importDomainTableCsv(id, csv, name, authSession) {
  const headers = await authHeaders(authSession);
  headers['Content-Type'] = 'text/csv';
  const res = await fetch(`${ECAP_BASE}/studio/api/domain-tables/${encodeURIComponent(id)}/import${name ? `?name=${encodeURIComponent(name)}` : ''}`, {
    method: 'POST', headers, body: csv,
  });
  if (!res.ok) throw new Error(`CSV import failed (${res.status})`);
  return res.json();
}
// ---- Tenants (admin console) ----
export const whoami = (a) => control('/studio/api/whoami', {}, a);
export const listTenants = (a) => control('/studio/api/tenants', {}, a);
// One-click onboarding: provisions the company (schema + Cognito group + secret vault) and, if adminEmail is
// given, invites its first admin user in the same step. Returns { tenant, invited?, inviteError? }.
export const provisionTenant = (id, name, adminEmail, a, template) =>
  control('/studio/api/tenants', { method: 'POST', body: JSON.stringify({ id, name, seedStarter: true, adminEmail: adminEmail || null, template: template || null }) }, a);

// Control-plane endpoint routing. getMyEndpoint drives which API base the UI calls for the active company;
// get/set (admin) configure a company's isolation tier (SHARED / DEDICATED) + its dedicated API base URL.
export const getMyEndpoint = (a) => control('/studio/api/tenant-endpoint', {}, a);
export const getTenantEndpoint = (id, a) => control(`/studio/api/tenants/${encodeURIComponent(id)}/endpoint`, {}, a);
export const setTenantEndpoint = (id, endpoint, a) =>
  control(`/studio/api/tenants/${encodeURIComponent(id)}/endpoint`, { method: 'PUT', body: JSON.stringify(endpoint) }, a);

// ---- Library monetization: per-tenant premium entitlements (admin grants which premium tools a company may use) ----
export const getPremiumCatalog = (a) => studio('/studio/api/entitlements/catalog', {}, a);
export const getTenantEntitlements = (id, a) => studio(`/studio/api/tenants/${encodeURIComponent(id)}/entitlements`, {}, a);
export const setTenantEntitlements = (id, capabilities, a) =>
  studio(`/studio/api/tenants/${encodeURIComponent(id)}/entitlements`, { method: 'PUT', body: JSON.stringify({ capabilities }) }, a);
export const getTenantUsage = (id, a) => studio(`/studio/api/tenants/${encodeURIComponent(id)}/usage`, {}, a);
// Billing: revenue by AI plugin per company, per month (pay-per-service).
export const getTenantBilling = (id, month, a) =>
  studio(`/studio/api/billing/tenants/${encodeURIComponent(id)}${month ? `?month=${encodeURIComponent(month)}` : ''}`, {}, a);

// ---- Generalized premium (any library type: capability/policy/aiProfile/validationTool/domainTable) ----
// Which items are marked premium for the current company (for library badges).
export const getPremiumMarks = (a) => studio('/studio/api/premium/marks', {}, a);
// Super-admin flags a library item premium/not-premium.
export const setPremiumMark = (type, id, premium, a) =>
  studio(`/studio/api/premium/marks/${encodeURIComponent(type)}/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify({ premium }) }, a);
// A company's premium catalogue (type:id refs) + what it's granted, and the grant setter.
export const getPremiumTenant = (id, a) => studio(`/studio/api/premium/tenants/${encodeURIComponent(id)}`, {}, a);
export const setPremiumGrants = (id, refs, a) =>
  studio(`/studio/api/premium/tenants/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify({ refs }) }, a);
export const getTenantWebhook = (id, a) => studio(`/studio/api/tenants/${encodeURIComponent(id)}/webhook`, {}, a);
export const setTenantWebhook = (id, leadWebhookUrl, a) =>
  studio(`/studio/api/tenants/${encodeURIComponent(id)}/webhook`, { method: 'PUT', body: JSON.stringify({ leadWebhookUrl }) }, a);

// Tenant-level outbound object (completed-lead push with auth via a Connection + dynamic payload mapping).
export const getTenantOutbound = (id, a) => studio(`/studio/api/tenants/${encodeURIComponent(id)}/outbound`, {}, a);
export const setTenantOutbound = (id, outbound, a) =>
  studio(`/studio/api/tenants/${encodeURIComponent(id)}/outbound`, { method: 'PUT', body: JSON.stringify(outbound) }, a);

// Notification channels — email/webhook fan-out on a completed lead (multi-channel, multi-recipient).
export const getTenantChannels = (id, a) => studio(`/studio/api/tenants/${encodeURIComponent(id)}/channels`, {}, a);
export const setTenantChannels = (id, channels, a) =>
  studio(`/studio/api/tenants/${encodeURIComponent(id)}/channels`, { method: 'PUT', body: JSON.stringify(channels) }, a);
export const testTenantChannel = (id, channel, recipient, a) =>
  studio(`/studio/api/tenants/${encodeURIComponent(id)}/channels/test`,
    { method: 'POST', body: JSON.stringify({ channel, recipient }) }, a);
// SES recipient verification (self-service, for the sandbox): trigger a verification email + read status.
// AI trace — every LLM call for the company (metadata + prompt/response), for debugging + token/cost.
export const getAiTraceSession = (sessionId, a) =>
  studio(`/studio/api/ai-trace/session/${encodeURIComponent(sessionId)}`, {}, a);
export const getAiTraceRecent = (a) => studio('/studio/api/ai-trace/recent', {}, a);
export const getAiUsage = (days, a) => studio(`/studio/api/ai-trace/usage?days=${days || 30}`, {}, a);

export const verifyTenantEmail = (id, email, a) =>
  studio(`/studio/api/tenants/${encodeURIComponent(id)}/channels/verify-email`,
    { method: 'POST', body: JSON.stringify({ email }) }, a);
export const tenantEmailStatus = (id, emails, a) =>
  studio(`/studio/api/tenants/${encodeURIComponent(id)}/channels/email-status`,
    { method: 'POST', body: JSON.stringify({ emails }) }, a);

// Per-company secret store (own isolated AWS Secrets Manager secret in prod). Values are write-only —
// only key names are ever returned. Connections reference these via ${secret:KEY}.
export const getTenantSecrets = (id, a) => studio(`/studio/api/tenants/${encodeURIComponent(id)}/secrets`, {}, a);
// Provision/repair a company's secret vault (store + encryption key) — idempotent. Returns { provisioned, keys }.
export const provisionTenantSecrets = (id, a) =>
  studio(`/studio/api/tenants/${encodeURIComponent(id)}/secrets`, { method: 'PUT' }, a);
export const putTenantSecret = (id, key, value, a) =>
  studio(`/studio/api/tenants/${encodeURIComponent(id)}/secrets/${encodeURIComponent(key)}`, { method: 'PUT', body: JSON.stringify({ value }) }, a);
export const deleteTenantSecret = (id, key, a) =>
  studio(`/studio/api/tenants/${encodeURIComponent(id)}/secrets/${encodeURIComponent(key)}`, { method: 'DELETE' }, a);

// Inbound integration API key (server-to-server; company reads/works its leads from its own app).
export const getTenantApiKey = (id, a) => studio(`/studio/api/tenants/${encodeURIComponent(id)}/api-key`, {}, a);
export const rotateTenantApiKey = (id, a) =>
  studio(`/studio/api/tenants/${encodeURIComponent(id)}/api-key/rotate`, { method: 'POST' }, a);

// Console access policy — which ECAP screens a company may use (HIDDEN/READ_ONLY/FULL), enforced server-side.
// Self: the current company's effective levels (drives the nav shell). Admin: get/set another company's policy.
export const getConsoleAccess = (a) => studio('/studio/api/console-access', {}, a);
export const getTenantConsoleAccess = (id, a) => studio(`/studio/api/tenants/${encodeURIComponent(id)}/console-access`, {}, a);
export const setTenantConsoleAccess = (id, access, a) =>
  studio(`/studio/api/tenants/${encodeURIComponent(id)}/console-access`, { method: 'PUT', body: JSON.stringify({ access }) }, a);

// ---- Tenant identity provider (OIDC / bring-your-own-SSO) — Continuous Customer Thread ----
// Per-tenant: point at your own OIDC issuer and pick the claim that becomes the end-user identity, so a
// signed-in user is recognised across sessions/channels and can resume a live-agent/approval conversation.
// Blank issuer or disabled → the platform Cognito default. Config = { issuer, jwksUri, audience,
// identityClaim, emailVerifiedRequired, enabled }; none of these are secrets (a client secret rides the vault).
export const getIdentityProvider = (a) => studio('/studio/api/identity-provider', {}, a);
export const setIdentityProvider = (cfg, a) =>
  studio('/studio/api/identity-provider', { method: 'PUT', body: JSON.stringify(cfg) }, a);
// Validate a sample user token against the SAVED config and show the identity it resolves to (claim override optional).
export const testIdentityToken = (token, identityClaim, a) =>
  studio('/studio/api/identity-provider/test', { method: 'POST', body: JSON.stringify({ token, identityClaim }) }, a);

// ---- Admin · schema-per-tenant DB migration (super-admin only) ----
// After Hibernate evolves the base `ecap` schema on boot, tenant schemas (ecap_<tenant>) drift; this
// syncs them back up additively (new tables/columns), backing up each altered table first. plan = dry-run
// (what would change per schema), run = apply. Both return { schemas:[{schema,version,statements,backups,
// applied,error}], ... }.
export const getMigrationPlan = (a) => control('/studio/api/admin/migration/plan', {}, a);
export const runMigration = (a) => control('/studio/api/admin/migration/run', { method: 'POST' }, a);
// Per-company: preview / migrate ONE selected schema (ecap_<tenant>) — pick a company, then migrate just them.
export const getMigrationPlanFor = (schema, a) => control(`/studio/api/admin/migration/plan/${encodeURIComponent(schema)}`, {}, a);
export const runMigrationFor = (schema, a) => control(`/studio/api/admin/migration/run/${encodeURIComponent(schema)}`, { method: 'POST' }, a);

// ---- LLM config (super-admin only; runtime model/temperature) ----
export const getLlmConfig = (a) => studio('/studio/api/llm-config', {}, a);
// patch: { model?, temperature?, ttlDays?, models? } — any subset.
export const setLlmConfig = (patch, a) =>
  studio('/studio/api/llm-config', { method: 'PUT', body: JSON.stringify(patch || {}) }, a);

// ---- Admin · user management (super-admin only; Cognito-backed) ----
// Fleet ops on the shared Cognito pool → control() (always the shared base, never a dedicated tenant stack).
export const adminListUsers = (a) => control('/studio/api/admin/users', {}, a);
export const adminCreateUser = (email, tenantId, admin, a) =>
  control('/studio/api/admin/users', { method: 'POST', body: JSON.stringify({ email, tenantId, admin }) }, a);
export const adminResetPassword = (username, a) =>
  control(`/studio/api/admin/users/${encodeURIComponent(username)}/reset-password`, { method: 'POST' }, a);
// Cognito group provisioning/repair per company: list existing groups, and ensure a company's group exists.
export const listCognitoGroups = (a) => control('/studio/api/admin/users/cognito-groups', {}, a);

// Studio-pool user lifecycle. Disable is reversible and keeps history; delete is permanent. The backend
// refuses either against your OWN login — locking the last super-admin out needs an AWS round-trip to fix.
export const adminSetUserEnabled = (username, enabled, a) =>
  control(`/studio/api/admin/users/${encodeURIComponent(username)}/enabled`,
    { method: 'POST', body: JSON.stringify({ enabled }) }, a);
export const adminDeleteUser = (username, a) =>
  control(`/studio/api/admin/users/${encodeURIComponent(username)}`, { method: 'DELETE' }, a);

// ---- Movorra staff on the ONE login (ride-share pool + movorra-admin) — MOVORRA-STAFF-1 ----
// Invite creates the ride-share login (Cognito emails a temporary password) or adds the group to an existing one.
// Reset / disable use the company-staff calls below (same pool, same username).
export const listMovorraStaff = (a) => control('/studio/api/admin/users/staff', {}, a);
export const inviteMovorraStaff = (email, a) =>
  control('/studio/api/admin/users/staff', { method: 'POST', body: JSON.stringify({ email }) }, a);

// ---- Company staff (the RIDE-SHARE Cognito pool) ----
// A customer company's own people — operations, dispatch, driver — live in the ride-share pool,
// because that is the pool movorra-api already trusts. Studio provisions them server-side through the AWS
// SDK, so no Studio token is ever trusted by movorra-api and no cross-issuer boundary moves.
// `role` is an allow-listed token (admin | dispatcher | driver), never a Cognito group name.
export const listCompanyUsers = (tenant, a) =>
  control(`/studio/api/admin/users/company?tenant=${encodeURIComponent(tenant)}`, {}, a);
export const createCompanyUser = (email, tenantId, role, a) =>
  control('/studio/api/admin/users/company',
    { method: 'POST', body: JSON.stringify({ email, tenantId, role }) }, a);
export const setCompanyUserEnabled = (username, enabled, a) =>
  control(`/studio/api/admin/users/company/${encodeURIComponent(username)}/enabled`,
    { method: 'POST', body: JSON.stringify({ enabled }) }, a);
export const resetCompanyUserPassword = (username, a) =>
  control(`/studio/api/admin/users/company/${encodeURIComponent(username)}/reset-password`,
    { method: 'POST' }, a);
export const deleteCompanyUser = (username, a) =>
  control(`/studio/api/admin/users/company/${encodeURIComponent(username)}`, { method: 'DELETE' }, a);
export const ensureTenantCognitoGroup = (id, a) =>
  control(`/studio/api/admin/users/cognito-groups/${encodeURIComponent(id)}`, { method: 'POST' }, a);

// ---- Operations analytics (company-wide funnel) ----
export const getAnalytics = (a) => studio('/studio/api/analytics', {}, a);
// Flow-specific operational breakdown — this flow's leads grouped by each captured attribute.
export const getFlowBreakdown = (flowId, a) =>
  studio(`/studio/api/analytics/flows/${encodeURIComponent(flowId)}/breakdown`, {}, a);

// ---- Operator alerts (hot-lead handoff inbox) ----
export const getAlerts = (all, a) => studio(`/studio/api/alerts${all ? '?all=true' : ''}`, {}, a);
export const ackAlert = (id, a) => studio(`/studio/api/alerts/${encodeURIComponent(id)}/ack`, { method: 'POST' }, a);

// ---- Sessions / Leads (captured conversations) ----
export const listSessions = (flowId, a) => studio(`/studio/api/flows/${encodeURIComponent(flowId)}/sessions`, {}, a);
export const getSession = (sessionId, a) => studio(`/studio/api/sessions/${encodeURIComponent(sessionId)}`, {}, a);
/** Update a lead's operational lifecycle (status / assignee / note) as an operator works it. */
export const updateLead = (sessionId, patch, a) =>
  studio(`/studio/api/sessions/${encodeURIComponent(sessionId)}/lead`, { method: 'PUT', body: JSON.stringify(patch) }, a);

export const getFlowKey = (id, authSession) => studio(`/studio/api/flows/${encodeURIComponent(id)}/key`, {}, authSession);
// Per-flow CORS: which browser origins may present this flow's key (empty = embeddable anywhere).
export const setFlowCors = (id, origins, authSession) =>
  studio(`/studio/api/flows/${encodeURIComponent(id)}/key/cors`, { method: 'PUT', body: JSON.stringify({ origins }) }, authSession);
export const getFlowAnalytics = (id, authSession) =>
  studio(`/studio/api/flows/${encodeURIComponent(id)}/analytics`, {}, authSession);
export const publishFlow = (id, authSession) =>
  studio(`/studio/api/flows/${encodeURIComponent(id)}/publish`, { method: 'POST' }, authSession);

// ---- Deploy Demo — a shareable, anonymous, version-PINNED demo link for a flow ----
// Deliberately separate from publishFlow: deploying/updating/taking down a demo never changes the flow's
// production live version. POST both deploys and updates (an update re-pins the snapshot but keeps the same
// token, so a link you already shared keeps working); DELETE takes it down without deleting the flow.
export const getFlowDemo = (id, a) => studio(`/studio/api/flows/${encodeURIComponent(id)}/demo`, {}, a);
export const deployFlowDemo = (id, a) =>
  studio(`/studio/api/flows/${encodeURIComponent(id)}/demo`, { method: 'POST' }, a);
export const takeDownFlowDemo = (id, a) =>
  studio(`/studio/api/flows/${encodeURIComponent(id)}/demo`, { method: 'DELETE' }, a);
// What KIND of workflow a flow is (QUOTE | GENERIC) — decides which UI its demo link renders.
export const setFlowType = (id, type, a) =>
  studio(`/studio/api/flows/${encodeURIComponent(id)}/type`, { method: 'PUT', body: JSON.stringify({ type }) }, a);

// The public demo URL for a token. A route on THIS app (same origin as Studio), so the demo reuses the real
// customer-facing chat UI rather than the generic embed widget.
export const demoPageUrl = (token) =>
  `${window.location.origin}/demo?key=${encodeURIComponent(token)}`;

// Public demo metadata — no auth, by design: this is what an anonymous visitor's browser calls to find out
// which interface to render. 404 means the token is unknown or the demo has been taken down.
export async function getDemoInfo(token) {
  const res = await fetch(`${ECAP_BASE}/runtime/demo?key=${encodeURIComponent(token)}`);
  if (!res.ok) {
    const err = new Error(`Demo unavailable (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

// ---- Runtime (public chat) — gated by the flow's endpoint key, no auth ----
// identityToken (optional): a verified end-user JWT — when supplied the runtime recognises the visitor and
// reconnects them to their canonical conversation (response carries resumed + status; see StartSessionResponse).
export async function startSession(publicKey, identityToken, opts) {
  let headers = { 'Content-Type': 'application/json', 'X-Movorra-Key': publicKey };
  // B5 draft tester: ?draft=true runs the session against the flow's WORKING DRAFT instead of the
  // published version — Studio-only, so the request must also carry the Studio auth headers.
  const draft = !!(opts && opts.draft);
  if (draft) headers = { ...(await authHeaders(opts.authSession)), ...headers };
  if (identityToken) headers['X-Movorra-Identity-Token'] = identityToken;
  const res = await fetch(`${ECAP_BASE}/runtime/sessions${draft ? '?draft=true' : ''}`, { method: 'POST', headers });
  if (!res.ok) throw new Error(`Could not start session (${res.status})`);
  return res.json();
}

/** Confirm payment for a session (the <PayNow/> button) → fills the gate attr and resumes to completion. */
export async function confirmPayment(sessionId, attribute, ref) {
  const res = await fetch(`${ECAP_BASE}/runtime/sessions/${encodeURIComponent(sessionId)}/pay`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ attribute: attribute || 'paid', ref: ref || null }),
  });
  if (!res.ok) {
    // ECAPAPI-PAY-OPAQUE: attach the status + the backend {error} message (was an opaque "failed (500)").
    let detail = `Payment confirm failed (${res.status})`;
    try { const b = await res.json(); detail = b.error || detail; } catch (e) { /* non-JSON */ }
    const err = new Error(detail); err.status = res.status; throw err;
  }
  return res.json();
}

export async function sendMessage(sessionId, text) {
  const res = await fetch(`${ECAP_BASE}/runtime/sessions/${encodeURIComponent(sessionId)}/messages`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ text }),
  });
  if (!res.ok) throw new Error(`Message failed (${res.status})`);
  return res.json();
}

/** Upload a file into a session (image/PDF) — optionally binding it to a FILE attribute. Public runtime. */
export async function uploadSessionFile(sessionId, file, attributeKey) {
  const fd = new FormData();
  fd.append('file', file);
  if (attributeKey) fd.append('attributeKey', attributeKey);
  const res = await fetch(`${ECAP_BASE}/runtime/sessions/${encodeURIComponent(sessionId)}/attachments`, {
    method: 'POST', body: fd,
  });
  if (!res.ok) {
    // ECAPAPI-UPLOAD-OPAQUE: attach the status (+ backend {error}) so callers can give actionable guidance
    // (e.g. a 413 "file too large") instead of an opaque failure.
    let detail = `Upload failed (${res.status})`;
    try { const b = await res.json(); detail = b.error || detail; } catch (e) { /* non-JSON */ }
    const err = new Error(detail); err.status = res.status; throw err;
  }
  return res.json();
}

/** Submit a component's result (e.g. a FileUpload's attribute) — resumes the engine and returns the reply. */
export async function submitComponentResult(sessionId, outputs) {
  const res = await fetch(`${ECAP_BASE}/runtime/sessions/${encodeURIComponent(sessionId)}/component-result`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ outputs: outputs || {} }),
  });
  if (!res.ok) throw new Error(`Component result failed (${res.status})`);
  return res.json();
}

/** The SSE stream URL for a turn (consume with EventSource for a streaming reply). */
export const streamUrl = (sessionId, text) =>
  `${ECAP_BASE}/runtime/sessions/${encodeURIComponent(sessionId)}/stream?message=${encodeURIComponent(text)}`;

export const ecapBase = () => ECAP_BASE;
